// Libraries
import { Flex, Text } from '@chakra-ui/react';
import { ComponentProps } from 'react';
// Components
// TODO: imported directly due to a bug when running a production build. Dive deep into import sequence later.
import { IconText } from '@atoms/IconText/IconText';
import { RatingInfoCompacted } from '@molecules/RatingInfoCompacted/RatingInfoCompacted';
// Types
import { FixedSizeArray } from '@globalTypes/index';

/**
 * Maps the cell component to the types allowed.
 */
export const CellMapper = {
  IconText: IconText,
  RatingInfoCompacted: RatingInfoCompacted,
  Text: Text,
};

/**
 * Represents the components map.
 */
export type CellMapperMap = typeof CellMapper;

/**
 * Represents the keys of the CellMapperMap type.
 */
export type ComponentsKeys = keyof CellMapperMap;

/**
 * Represents the cell types.
 */
export type CellTypes = {
  /**
   * Each key in CellTypes corresponds to a component name,
   * and its value represents the props type for that component.
   */
  [Key in keyof CellMapperMap]: ComponentProps<CellMapperMap[Key]>;
};

/**
 * Represents a cell in the table.
 * @template Key - The key of the component in CellMapperMap.
 * @template Types - The available cell types.
 */
export type Cell<Key extends keyof Types, Types> = {
  /**
   * The name of the component.
   */
  component: Key;
  /**
   * The props for the component.
   */
  props: Types[Key];
  /**
   * The alignment of the content.
   */
  align?: ComponentProps<typeof Flex>['justifyContent'];
};

/**
 * Represents a cell in the table.
 * @template Types - The available cell types.
 */
export type Cells<Types> = {
  /**
   * Union of all possible cells in the table,
   * based on the keys of the available cell types.
   */
  [Key in keyof Types]: Cell<Key, Types>;
}[keyof Types];

/**
 * Represents a row in the table.
 * @template NumCells - The number of cells.
 * @template Types - The available cell types.
 */
export type CardTableRow<NumCells extends number> = {
  /**
   * An array of cells in the row.
   */
  cells: FixedSizeArray<NumCells, Cells<CellTypes>>;
};

/**
 * Represents a caption in the table.
 */
export type Caption = {
  /**
   * The identifier of the caption.
   */
  id: string;
  /**
   * The label of the caption.
   */
  label: string;
  /**
   * The alignment of the content.
   */
  align?: ComponentProps<typeof Flex>['justifyContent'];
  /**
   * The message to display in the tooltip
   */
  tooltip?: string;
};

/**
 * Represents an array of fixed size captions.
 * @template NumCells - The number of cells.
 */
export type Captions<NumCells extends number> = FixedSizeArray<NumCells, Caption>;

/**
 * Represents a table.
 * @template NumCells - The number of cells.
 */
export type CardTableType<NumCells extends number> = {
  /**
   * An array of rows in the table.
   */
  rows: CardTableRow<NumCells>[];
  /**
   * An array of headers in the table.
   */
  headers: Captions<NumCells>;
  /**
   * An array of footers in the table (optional).
   */
  footers?: Captions<NumCells>;
};

/**
 * Represents the possible variants of the table.
 */
enum Variants {
  cardTable = 'cardTable',
  cardLightTable = 'cardLightTable',
}

/**
 * Represents the variant keys of the table.
 */
export type VariantsKeys = keyof typeof Variants;

/**
 * Represents the props for a card table component.
 * @template NumCells - The number of cells.
 */
export type CardTableProps<NumCells extends number> = {
  /**
   * The table data.
   */
  table: CardTableType<NumCells>;
  /**
   * The variant of the table.
   */
  variant: keyof typeof Variants;
  /**
   * The title of the table (optional).
   */
  title?: string;
};
